import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Language as LanguageIcon,
  Share as SocialIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import { 
  getSiteSettings, 
  updateSiteSettings, 
  CMSSiteSettings 
} from '../../../../firebase/services/cmsService';

const SiteSettingsPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [settings, setSettings] = useState<Partial<CMSSiteSettings>>({
    siteName: '',
    siteDescription: '',
    siteKeywords: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
    },
    businessHours: {
      monday: { open: '09:00', close: '17:00', closed: false },
      tuesday: { open: '09:00', close: '17:00', closed: false },
      wednesday: { open: '09:00', close: '17:00', closed: false },
      thursday: { open: '09:00', close: '17:00', closed: false },
      friday: { open: '09:00', close: '17:00', closed: false },
      saturday: { open: '10:00', close: '16:00', closed: false },
      sunday: { open: '10:00', close: '16:00', closed: true },
    },
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const siteSettings = await getSiteSettings();
      if (siteSettings) {
        setSettings(siteSettings);
      }
    } catch (err: any) {
      console.error('Error loading site settings:', err);
      setError(err.message || 'Failed to load site settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSocialMediaChange = (platform: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: value,
      },
    }));
  };

  const handleBusinessHoursChange = (day: string, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...prev.businessHours?.[day],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const userId = userProfile?.uid || 'unknown';
      await updateSiteSettings(settings, userId);
      
      setSuccess('Site settings saved successfully!');
    } catch (err: any) {
      console.error('Error saving site settings:', err);
      setError(err.message || 'Failed to save site settings');
    } finally {
      setSaving(false);
    }
  };

  const getDayName = (day: string) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              <LanguageIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Site Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Configure global website settings and information
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Save Settings
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* General Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="General Information"
                avatar={<BusinessIcon />}
              />
              <CardContent>
                <TextField
                  fullWidth
                  label="Site Name"
                  value={settings.siteName || ''}
                  onChange={(e) => handleInputChange('siteName', e.target.value)}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Site Description"
                  value={settings.siteDescription || ''}
                  onChange={(e) => handleInputChange('siteDescription', e.target.value)}
                  multiline
                  rows={3}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Site Keywords"
                  value={settings.siteKeywords || ''}
                  onChange={(e) => handleInputChange('siteKeywords', e.target.value)}
                  helperText="Comma-separated keywords for SEO"
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Logo URL"
                  value={settings.logo || ''}
                  onChange={(e) => handleInputChange('logo', e.target.value)}
                  helperText="URL to your site logo"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="Contact Information"
                avatar={<PhoneIcon />}
              />
              <CardContent>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  value={settings.contactEmail || ''}
                  onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={settings.contactPhone || ''}
                  onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Address"
                  value={settings.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  multiline
                  rows={3}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Social Media */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="Social Media"
                avatar={<SocialIcon />}
              />
              <CardContent>
                <TextField
                  fullWidth
                  label="Facebook URL"
                  value={settings.socialMedia?.facebook || ''}
                  onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Instagram URL"
                  value={settings.socialMedia?.instagram || ''}
                  onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="Twitter URL"
                  value={settings.socialMedia?.twitter || ''}
                  onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="LinkedIn URL"
                  value={settings.socialMedia?.linkedin || ''}
                  onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Business Hours */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title="Business Hours"
                avatar={<ScheduleIcon />}
              />
              <CardContent>
                {Object.entries(settings.businessHours || {}).map(([day, hours]) => (
                  <Box key={day} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {getDayName(day)}
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!hours.closed}
                              onChange={(e) => handleBusinessHoursChange(day, 'closed', !e.target.checked)}
                            />
                          }
                          label={hours.closed ? 'Closed' : 'Open'}
                        />
                      </Grid>
                      {!hours.closed && (
                        <>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Open"
                              type="time"
                              value={hours.open || '09:00'}
                              onChange={(e) => handleBusinessHoursChange(day, 'open', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Close"
                              type="time"
                              value={hours.close || '17:00'}
                              onChange={(e) => handleBusinessHoursChange(day, 'close', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Save Button (Footer) */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            Save All Settings
          </Button>
        </Box>
      </Box>
  );
};

export default SiteSettingsPage; 